import { useCallback, useEffect, useState } from 'react';
import { LocationParams } from '../components/search/admin-dashboard-tables/interfaces';
import fetchSearchParams from '../utils/fetchSearchParams';
import { ALGOLIA_SORT_BY_LOCATION_IF_SET } from '../utils/constants';

const useRequestLocation = (
  waitTime?: number,
  shouldSortByLocationOnLoad?: boolean,
) => {
  const fetchInitialSearchLocation = () => {
    const {
      lat,
      lng,
    }: LocationParams = fetchSearchParams();
    if (lat && lng) {
      return `${lat}, ${lng}`;
    }
    if (localStorage.getItem('latitude') && localStorage.getItem('longitude')) {
      return `${localStorage.getItem('latitude')}, ${localStorage.getItem('longitude')}`;
    }
    return '';
  };

  const fetchInitialUseSearchByLocation = () => {
    const {
      lat,
      lng,
    }: LocationParams = fetchSearchParams();
    return !!(lat && lng);
  };

  const [searchLocation, setSearchLocation] = useState(fetchInitialSearchLocation());
  const [showRequestLocation, setShowRequestLocation] = useState(false);
  const [isAuto, setIsAuto] = useState(false);
  const [locationIsLoading, setLocationIsLoading] = useState(false);
  const [useSearchByLocation, setUseSearchByLocation] = useState(
    shouldSortByLocationOnLoad || fetchInitialUseSearchByLocation(),
  );

  const requestLocation = (location: string) => {
    const lastDisabledTime = localStorage.getItem('disable-popup-location');

    let shouldShowLocationRequest = true;

    if (lastDisabledTime) {
      const disableLastDate = new Date(lastDisabledTime);
      const now = new Date();
      const diff = now.getTime() - disableLastDate.getTime();

      const checkAfter = 72 * 60 * 60 * 1000; // 72 hours

      if (diff <= checkAfter) {
        shouldShowLocationRequest = false;
      }
    }

    if (!location && shouldShowLocationRequest) {
      setIsAuto(true);
      setShowRequestLocation(true);
    }
  };

  const closeModal = useCallback(() => {
    if (isAuto) {
      setIsAuto(false);
    }
    setShowRequestLocation(false);
  }, [isAuto]);

  const updateLocation = useCallback((e: any) => {
    const { detail } = e as { detail: { newLocation: boolean } };
    if (detail && detail.newLocation) {
      if (localStorage.getItem('latitude') && localStorage.getItem('longitude')) {
        const newLocation = `${localStorage.getItem('latitude')}, ${localStorage.getItem('longitude')}`;
        if (newLocation !== searchLocation) {
          setSearchLocation(newLocation);
          setUseSearchByLocation((currentUseSearchByLocation) => {
            // If already searching by location, keep doing so;
            // otherwise, turn on, if constant is set.
            return currentUseSearchByLocation || ALGOLIA_SORT_BY_LOCATION_IF_SET;
          });
        }
      }
    }
  }, [searchLocation]);

  useEffect(() => {
    document.addEventListener('updateLocation', updateLocation);
    return () => document.removeEventListener('updateLocation', updateLocation);
  }, [updateLocation]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (waitTime) {
      timer = setTimeout(
        () => requestLocation(searchLocation),
        waitTime,
      );
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [searchLocation, waitTime]);

  const checkLocation = useCallback(() => {
    if (!useSearchByLocation) {
      if (searchLocation) {
        setUseSearchByLocation(true);
      } else {
        setShowRequestLocation(true);
      }
    } else {
      setUseSearchByLocation(false);
    }
  }, [searchLocation, useSearchByLocation]);

  const toggleSearchByLocation = useCallback((on: boolean) => {
    if (on && !useSearchByLocation) {
      if (searchLocation) {
        setUseSearchByLocation(true);
      } else {
        setShowRequestLocation(true);
      }
    } else {
      setUseSearchByLocation(false);
    }
  }, [searchLocation, useSearchByLocation]);

  const stopAsking = () => {
    localStorage.setItem('disable-popup-location', new Date().toISOString());
    closeModal();
  };

  const onCompletionCallback = (toggleLocationSearchOn = false) => {
    closeModal();
    setLocationIsLoading(false);
    setSearchLocation(`${localStorage.getItem('latitude')}, ${localStorage.getItem('longitude')}`);
    if (toggleLocationSearchOn) {
      setUseSearchByLocation(true);
    }
  };

  return {
    searchLocation,
    showRequestLocation,
    useSearchByLocation,
    checkLocation,
    closeModal,
    locationIsLoading,
    setLocationIsLoading,
    stopAsking,
    isAuto,
    onCompletionCallback,
    toggleSearchByLocation,
  };
};

export default useRequestLocation;
